import {Slide} from '@aboutyou/transitions';
import React, {forwardRef, PropsWithChildren, useEffect, useState} from 'react';
import styled, {css} from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {tabletAndUp} from '../../../constants/customMedia';
import {Z_INDEXES} from '../../../constants/layers';
import {Icon} from '../Icon/Icon';
import {GenericIconName} from '../Icon/IconName';
import {useToastTimeout} from './helpers';

export type ToastTypes =
  | 'FAILURE'
  | 'NEUTRAL'
  | 'PRIMARY'
  | 'SUCCESS'
  | 'WARNING';

export type ToastProps = PropsWithChildren<{
  autoHide?: boolean;
  autoHideTimeoutMs?: number;
  className?: string;
  isShown: boolean;
  hideCloseButton?: boolean;
  /** Called when the toast disappears after the specified timeout */
  onAutoClose?(): void;
  /**
   * Called when the close button is tapped/clicked
   * The button is hidden if this function is not provided
   */
  onDismiss?(): void;
  detachedFromBorders?: boolean;
  type: ToastTypes;
  direction?: 'up' | 'down' | 'left' | 'right';
  offset?: string;
  position?: 'top left' | 'top right' | 'bottom left' | 'bottom right';
}>;

/**
 * @deprecated only for internal use, please use ToastContext from @aboutyou/ui-components instead
 */
export const Toast = forwardRef<HTMLDivElement, ToastProps>(function Toast(
  {
    autoHide: defaultAutoHide,
    autoHideTimeoutMs = 4000,
    detachedFromBorders = false,
    children,
    className,
    isShown,
    onAutoClose,
    onDismiss,
    hideCloseButton,
    type,
    direction,
    offset,
    position = 'bottom right',
    ...rest
  },
  ref,
) {
  const [autoHide, setAutoHide] = useState(defaultAutoHide);
  const [timerIsRunning, runTimer] = useToastTimeout(autoHideTimeoutMs);
  const [hasTimerStarted, setHasTimerStarted] = useState(false);

  useEffect(() => {
    if (isShown && autoHide) {
      runTimer();
      // eslint-disable-next-line local-rules/set-state-in-use-effect -- TODO: Cleanup setState in useEffect https://aboutyou.atlassian.net/browse/ASFE-7602
      setHasTimerStarted(true);
    }
  }, [isShown, autoHide, runTimer]);

  useEffect(() => {
    if (hasTimerStarted && !timerIsRunning) {
      onAutoClose?.();
      // eslint-disable-next-line local-rules/set-state-in-use-effect -- TODO: Cleanup setState in useEffect https://aboutyou.atlassian.net/browse/ASFE-7602
      setHasTimerStarted(false);
    }
  }, [timerIsRunning, onAutoClose, hasTimerStarted]);

  const handleOnDismiss = () => {
    setAutoHide(false);
    onDismiss?.();
  };

  return (
    <Slide mounted={isShown} duration={550} direction={direction || 'up'}>
      <Wrapper
        ref={ref}
        className={className}
        detachedFromBorders={detachedFromBorders}
        type={type}
        direction={direction || 'up'}
        offset={offset}
        position={position}
        data-testid="toast"
        {...rest}
      >
        <Content data-testid="toast-content">{children}</Content>
        {onDismiss && !hideCloseButton && (
          <CloseButton onClick={handleOnDismiss}>
            <CloseIcon asset={GenericIconName.Close} size={'XS'} />
          </CloseButton>
        )}
      </Wrapper>
    </Slide>
  );
});

const Wrapper = styled.div<
  {type: ToastTypes} & Pick<
    ToastProps,
    'detachedFromBorders' | 'direction' | 'offset' | 'position'
  >
>`
  display: flex;
  position: fixed;
  ${({direction, offset}) =>
    direction === 'up'
      ? css`
          bottom: ${offset ?? 0};
        `
      : css`
          top: ${offset ?? 0};
        `};
  left: 0;
  padding: 0.8rem;
  box-sizing: border-box;
  font-size: 14px;
  text-align: center;
  line-height: 1.5;
  box-sizing: border-box;
  width: 100%;
  z-index: ${Z_INDEXES.TOAST_NOTIFICATION};

  ${tabletAndUp(css`
    padding-top: 0;
    padding-bottom: 0;
  `)}

  ${({detachedFromBorders, offset, position}) =>
    detachedFromBorders &&
    css`
      left: 0;
      right: 0;
      margin: auto;

      ${position === 'bottom right' || position === 'bottom left'
        ? css`
            bottom: ${offset ? `calc(${offset} + 1rem)` : '1rem'};
            top: initial;
          `
        : ''}

      ${position === 'top right' || position === 'top left'
        ? css`
            bottom: initial;
            top: ${offset ? `calc(${offset} + 1rem)` : '1rem'};
          `
        : ''}

      ${tabletAndUp(css`
        max-width: 450px;

        margin: unset;
        padding: 1.2rem;

        ${position === 'bottom right' &&
        css`
          bottom: ${offset ? `calc(${offset} + 1rem)` : '1rem'};
          top: initial;
          right: 1rem;
          left: unset;
        `}

        ${position === 'bottom left' &&
        css`
          bottom: ${offset ? `calc(${offset} + 1rem)` : '1rem'};
          top: initial;
          right: unset;
          left: 1rem;
        `}

        ${position === 'top right' &&
        css`
          top: ${offset ? `calc(${offset} + 1rem)` : '1rem'};
          bottom: initial;
          right: 1rem;
          left: unset;
        `}

        ${position === 'top left' &&
        css`
          bottom: initial;
          top: ${offset ? `calc(${offset} + 1rem)` : '1rem'};
          right: unset;
          left: 1rem;
        `}
      `)}

      width: calc(100% - 2rem);
      border-radius: 10px;
      box-shadow: 0 10px 15px 0 rgba(0, 0, 0, 0.08),
        0 2px 2px 0 rgba(0, 0, 0, 0.05), 0 -2px 2px 0 rgba(0, 0, 0, 0.02);
    `};

  background-color: ${({type}) => {
    switch (type) {
      case 'FAILURE':
        return COLORS.Red;
      case 'PRIMARY':
        return COLORS.Black;
      case 'SUCCESS':
        return COLORS.Green;
      case 'WARNING':
        return COLORS.Yellow;
      default:
        return COLORS.Grey4;
    }
  }};

  color: ${({type}) => {
    switch (type) {
      case 'PRIMARY':
        return COLORS.White;
      default:
        return COLORS.Black;
    }
  }};
`;

const CloseButton = styled.button`
  border: none;
  background-color: transparent;
  margin-left: auto;
  flex-grow: 0;

  &:focus {
    outline: none;
  }
`;

const CloseIcon = styled(Icon)`
  fill: rgba(0, 0, 0, 0.5);
`;

const Content = styled('span')`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: 100%;
  ${tabletAndUp(css`
    padding: 0.5rem;
  `)}
`;
